import { useState, useCallback, useEffect } from 'react';
import { DEFAULT_SECTION } from '../config';

/**
 * Hook for managing admin panel state
 * 
 * @param {Object} options
 * @param {Function} options.onClose - Callback when panel should close
 * @returns {Object} Panel state and handlers
 */
const useAdminPanel = ({ onClose }) => {
  const [activeSection, setActiveSection] = useState(DEFAULT_SECTION);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  /**
   * Handle navigation item click
   */
  const handleNavigation = useCallback((sectionId) => {
    setActiveSection(sectionId);
    // Close mobile nav when item selected
    setIsMobileNavOpen(false);
  }, []);

  /**
   * Toggle mobile navigation drawer
   */
  const toggleMobileNav = useCallback(() => {
    setIsMobileNavOpen(prev => !prev);
  }, []);

  /**
   * Close mobile navigation drawer
   */
  const closeMobileNav = useCallback(() => {
    setIsMobileNavOpen(false);
  }, []);

  /**
   * Handle ESC key to close panel
   */
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        // If mobile nav is open, close it first
        if (isMobileNavOpen) {
          setIsMobileNavOpen(false);
        } else {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isMobileNavOpen]);

  /**
   * Prevent body scroll when panel is open
   */
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  return {
    activeSection,
    setActiveSection: handleNavigation,
    isMobileNavOpen,
    toggleMobileNav,
    closeMobileNav
  };
};

export default useAdminPanel;



